import { useCallback } from 'react';

import { useQueries, useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { isSameCurrency, previewStaleTime, queryKeys } from '@splitfiesta/core';
import type { ImportRatePair } from '@splitfiesta/core';
import type {
  ExchangeRatePreviewDto,
  SuccessResponseDto,
} from '@splitfiesta/shared';

import { useTypedApi } from '@/hooks/useTypedApi';

type TypedApi = ReturnType<typeof useTypedApi>;

type ExchangeRateResult = { rate: number; rateDate: string };

function exchangeRateQueryOptions(
  api: TypedApi,
  p: { from: string; to: string; date: string; enabled?: boolean }
) {
  const from = p.from.toUpperCase();
  const to = p.to.toUpperCase();
  return {
    queryKey: queryKeys.exchangeRate(from, to, p.date),
    queryFn: async () => {
      const res = (await api.exchangeRates.get({
        from,
        to,
        date: p.date,
      })) as SuccessResponseDto<ExchangeRatePreviewDto>;
      return { rate: res.data.rate, rateDate: res.data.rateDate };
    },
    enabled: (p.enabled ?? true) && !!p.date && !isSameCurrency(from, to),
    staleTime: previewStaleTime(p.date),
    // Persisted: a closed-day rate never changes, so a stale pin from a
    // prior session is still safe to reuse rather than refetching. Kept to
    // 24h rather than the shared `OFFLINE_GC_TIME_MS` — a rate preview is
    // cheap to refetch and there is no offline-trip case to protect here.
    gcTime: 1000 * 60 * 60 * 24,
    retry: (count: number, err: unknown) =>
      count < 2 &&
      !(err as { status?: number })?.status?.toString().startsWith('4'),
  };
}

/**
 * Foreign-currency preview rate for the expense forms. A thin wrapper
 * around `useQuery` — `useExpenseForm` maps the raw TanStack result
 * (`status`/`fetchStatus`) into the four-value `preview.status` surface
 * consumers see.
 */
export function useExchangeRateQuery(p: {
  from: string;
  to: string;
  date: string;
  enabled?: boolean;
}) {
  const api = useTypedApi();
  return useQuery(exchangeRateQueryOptions(api, p));
}

/**
 * A rate per `{ currency, date }` pair, shaped for `convertedTotalsPreview`:
 * the returned lookup answers `undefined` until that pair's rate has
 * resolved, so a loading, failed or disabled lookup simply renders nothing.
 */
export function useExchangeRatesQuery(p: {
  pairs: ImportRatePair[];
  to: string;
  enabled?: boolean;
}): (currency: string, date: string) => number | undefined {
  const api = useTypedApi();
  // Stable as long as the caller memoises `pairs` (both do): an inline
  // `combine` is a new function every render, which makes query-core redo the
  // merge and hand back a fresh lookup each time.
  const combine = useCallback(
    (results: UseQueryResult<ExchangeRateResult>[]) => {
      const rates = new Map<string, number>();
      results.forEach((result, index) => {
        const pair = p.pairs[index];
        if (result.data && pair) {
          rates.set(
            `${pair.currency.toUpperCase()}|${pair.date}`,
            result.data.rate
          );
        }
      });
      return (currency: string, date: string) =>
        rates.get(`${currency.toUpperCase()}|${date}`);
    },
    [p.pairs]
  );
  return useQueries({
    queries: p.pairs.map(pair =>
      exchangeRateQueryOptions(api, {
        from: pair.currency,
        to: p.to,
        date: pair.date,
        enabled: p.enabled,
      })
    ),
    combine,
  });
}
